import { TechnicalError } from '../../models/errors.model';
import * as RecoveryMethodsActions from '../actions/password-recovery-management.actions';
import { PasswordRecoveryManagement } from '../../models/password-recovery-management.model';
import { SuccessCode, SuccessInfo } from '../../models/success.model';

export interface PasswordRecoveryManagementState {
  passwordRecoveryManagement: PasswordRecoveryManagement;
  error: TechnicalError;
  success: SuccessInfo;
}

export const initialState: PasswordRecoveryManagementState = {
  passwordRecoveryManagement: null,
  error: null,
  success: null,
};

export function reducer(
  state = initialState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  switch (action.type) {
    case RecoveryMethodsActions.ActionTypes.FOUND_RECOVERY_METHODS:
      return RecoveryMethodsFoundSuccess(state, action);
    case RecoveryMethodsActions.ActionTypes.SET_EMAIL_RECOVERY_METHOD_SUCCESS:
      return setEmailRecoveryMethodSuccess(state, action);
    case RecoveryMethodsActions.ActionTypes.SET_EMAIL_RECOVERY_METHOD_FAIL:
      return setEmailRecoveryMethodFail(state, action);
    case RecoveryMethodsActions.ActionTypes.SET_SMS_RECOVERY_METHOD_SUCCESS:
      return setSmsRecoveryMethodSuccess(state, action);
    case RecoveryMethodsActions.ActionTypes.SET_SMS_RECOVERY_METHOD_FAIL:
      return setSmsRecoveryMethodFail(state, action);
    case RecoveryMethodsActions.ActionTypes
      .ACTIVATE_SMS_RECOVERY_METHOD_SUCCESS:
      return activateSmsRecoveryMethodSuccess(state, action);
    case RecoveryMethodsActions.ActionTypes.ACTIVATE_SMS_RECOVERY_METHOD_FAIL:
      return activateSmsRecoveryMethodFail(state, action);
    case RecoveryMethodsActions.ActionTypes.RESET_ERROR:
      return resetError(state, action);
    case RecoveryMethodsActions.ActionTypes.RESET_SUCCESS:
      return resetSuccess(state, action);
    default:
      return state;
  }
}

function RecoveryMethodsFoundSuccess(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  return {
    passwordRecoveryManagement: action.payload,
    error: null,
    success: null,
  };
}

function setEmailRecoveryMethodSuccess(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  const newPasswordRecoveryManagement = Object.assign(
    {},
    state.passwordRecoveryManagement,
  );
  newPasswordRecoveryManagement.email = {
    mail: action.payload,
    status: 'pending',
  };
  return {
    ...state,
    passwordRecoveryManagement: newPasswordRecoveryManagement,
    error: null,
    success: {
      code: SuccessCode.PasswordRecoveryEmailMethodSuccess,
    },
  };
}

function setEmailRecoveryMethodFail(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  return {
    ...state,
    error: action.payload || {},
    success: null,
  };
}

function setSmsRecoveryMethodSuccess(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  const newPasswordRecoveryManagement = Object.assign(
    {},
    state.passwordRecoveryManagement,
  );
  newPasswordRecoveryManagement.sms = {
    mobile: action.payload,
    status: 'pending',
  };
  return {
    ...state,
    passwordRecoveryManagement: newPasswordRecoveryManagement,
    error: null,
    success: {
      code: SuccessCode.PasswordRecoverySmsMethodSuccess,
    },
  };
}

function setSmsRecoveryMethodFail(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  return {
    ...state,
    error: action.payload || {},
    success: null,
  };
}

function activateSmsRecoveryMethodSuccess(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  const newPasswordRecoveryManagement = Object.assign(
    {},
    state.passwordRecoveryManagement,
  );
  newPasswordRecoveryManagement.sms = {
    ...state.passwordRecoveryManagement.sms,
    status: 'active',
  };
  return {
    ...state,
    passwordRecoveryManagement: newPasswordRecoveryManagement,
    error: null,
    success: {
      code: SuccessCode.PasswordRecoverySmsMethodActivateSuccess,
    },
  };
}

function activateSmsRecoveryMethodFail(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  return {
    ...state,
    error: action.payload || {},
    success: null,
  };
}

function resetSuccess(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  return {
    ...state,
    success: null,
  };
}

function resetError(
  state: PasswordRecoveryManagementState,
  action: RecoveryMethodsActions.Actions,
): PasswordRecoveryManagementState {
  return {
    ...state,
    error: null,
  };
}

export const getPasswordRecoveryManagement = (
  state: PasswordRecoveryManagementState,
) => state.passwordRecoveryManagement;
export const getError = (state: PasswordRecoveryManagementState) => state.error;
export const getSuccess = (state: PasswordRecoveryManagementState) =>
  state.success;
